import { MicOff, Radio, ShieldAlert, Volume2 } from "lucide-react";
import type { PublicPlayer, VoiceSettings } from "@ma-soi/game-core";

const SETTING_LABELS: Array<[keyof VoiceSettings, string, string]> = [
  ["enabled", "Bật voice chat", "Cho phép âm thanh trong phòng."],
  ["lobbyVoice", "Voice sảnh chờ", "Mọi người thử micro trước ván."],
  ["dayVoice", "Voice ban ngày", "Người sống được thảo luận."],
  ["werewolfNightVoice", "Kênh Ma Sói", "Ma Sói thì thầm riêng ban đêm."],
  ["deadCanListen", "Người chết được nghe", "Linh hồn nghe thảo luận ban ngày."],
  ["ghostVoiceChat", "Kênh linh hồn", "Người chết nói riêng với nhau."],
  ["muteDuringVoting", "Tắt micro khi bỏ phiếu", "Giữ im lặng lúc phán quyết."],
  ["pushToTalk", "Push-to-talk", "Giữ nút để nói."],
  ["voiceActivityDetection", "Nhận diện đang nói", "Hiện vòng sáng khi có tiếng."],
  ["duckBackgroundMusic", "Giảm nhạc nền", "Hạ ambience khi có voice."],
];

export function HostVoiceControls({
  players,
  settings,
  hostMutedPlayerIds,
  busy,
  onCommand,
  onSettingsPatch,
}: {
  players: PublicPlayer[];
  settings: VoiceSettings;
  hostMutedPlayerIds: string[];
  busy: boolean;
  onCommand: (command: string, payload?: Record<string, unknown>) => Promise<unknown>;
  onSettingsPatch: (settings: Partial<VoiceSettings>) => Promise<unknown>;
}) {
  return (
    <section className="host-voice-controls surface-panel p-4">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <p className="eyebrow">Bảng quản trò</p>
          <h3 className="panel-title flex items-center gap-2">
            <Radio className="size-4 text-amber-400" />
            Điều khiển voice
          </h3>
        </div>
        <ShieldAlert className="size-5 text-red-300" />
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        <button className="ghost-button small" disabled={busy} onClick={() => void onCommand(settings.enabled ? "voiceDisable" : "voiceEnable").catch(() => undefined)}>
          <Volume2 className="size-4" />
          {settings.enabled ? "Tắt voice" : "Bật voice"}
        </button>
        <button className="ghost-button small" disabled={busy} onClick={() => void onCommand(settings.mutedAll ? "voiceUnmuteAll" : "voiceMuteAll").catch(() => undefined)}>
          <MicOff className="size-4" />
          {settings.mutedAll ? "Mở tất cả" : "Khóa tất cả"}
        </button>
        <button className="ghost-button small" disabled={busy} onClick={() => void onCommand(settings.listenOnlyMode ? "voiceOpen" : "voiceListenOnly").catch(() => undefined)}>
          {settings.listenOnlyMode ? "Mở quyền nói" : "Chỉ nghe"}
        </button>
      </div>

      <div className="voice-settings-grid">
        {SETTING_LABELS.map(([key, title, hint]) => (
          <label key={key} className="setting-check settings-option">
            <input
              type="checkbox"
              checked={Boolean(settings[key])}
              disabled={busy}
              onChange={(event) => void onSettingsPatch({ [key]: event.target.checked }).catch(() => undefined)}
            />
            <span>
              <strong>{title}</strong>
              <small>{hint}</small>
            </span>
          </label>
        ))}
      </div>

      <div className="mt-4 grid gap-2 sm:grid-cols-2">
        {players.map((player) => {
          const muted = hostMutedPlayerIds.includes(player.uid);
          return (
            <button
              key={player.uid}
              className={`voice-host-player ${muted ? "muted" : ""}`}
              disabled={busy}
              onClick={() => void onCommand(muted ? "voiceUnmute" : "voiceMute", { targetId: player.uid }).catch(() => undefined)}
            >
              <span className="truncate">{player.name}</span>
              <small>{muted ? "Mở micro" : "Khóa micro"}</small>
            </button>
          );
        })}
      </div>
    </section>
  );
}
